// Legal Pages Translations - Terms of Service & Privacy Policy
export const legalTranslations = {
  en: {
    // Terms of Service
    terms: {
      pageTitle: 'Middleman Terms of Service',
      lastUpdated: 'Last updated: January 2025',
      
      intro: {
        title: 'Introduction',
        p1: 'These Terms of Service (the "Terms") constitute a contract between you (hereinafter referred to, as the context requires, as "user," "member," "buyer," or "seller") and Middleman. These Terms govern your use of the site and the services offered by Middleman.',
        p2: 'At any time, Middleman reserves the right to modify these Terms. Your use of the site confirms that you agree to abide by these Terms as stated and amended from time to time. If you cannot commit to abiding by these Terms, you must immediately cease using the site and services offered by Middleman.'
      },
      
      service: {
        title: 'Nature of Service Offered',
        intermediary: {
          title: 'Intermediary and Validity Verification Service',
          p1: 'Middleman acts as a trusted intermediary facilitating secure transactions of verifiable digital tickets issued by authorized ticketing services (including Ticketmaster, Evenko, and other similar platforms) between a seller and a buyer acting on their own behalf (as individuals).',
          p2: 'We act exclusively as an intermediary by ensuring the validity of exchanged tickets to reduce fraud risks and offer a secure transaction between the Seller and the Buyer. Middleman verifies the apparent validity of tickets at the time of the transaction, without additional guarantee.',
          p3: 'Middleman is not a ticket seller or reseller.'
        },
        cancellation: {
          title: 'Responsibility in Case of Event Cancellation',
          p1: 'In case of event cancellation or modification, Middleman assumes no responsibility for the event organization. Ticket holders must refer directly to the organization that originally issued the tickets or to the event organizer for any information, postponement, or refund requests. Refunds will be processed according to the terms set by the original ticket issuer.'
        },
        role: {
          title: 'Intermediary Role',
          p1: 'Middleman acts solely as a transaction intermediary. Although we ensure ticket validity at the time of the transaction, Middleman assumes no responsibility for event organization or any changes made by the organizer.'
        },
        limitation: {
          title: 'Limitation',
          p1: 'Middleman shall not be held liable for any financial loss, damage, or other prejudice resulting from event cancellation or postponement, nor for disputes between the Seller and Buyer regarding ticket use or value.'
        }
      },
      
      eligibility: {
        title: 'Eligibility',
        p1: 'Middleman services are reserved for individuals who are at least eighteen (18) years old, or who have reached the legal age of majority in their jurisdiction. Any person registering on the site must be able to provide identity verification documents upon request.'
      },
      
      fees: {
        title: 'Platform Fees',
        p1: 'Middleman charges a tiered service fee based on the total transfer amount: $6.95 for transfers of $125 or less, $9.95 for transfers from $125.01 to $250, and $19.95 for transfers over $250. This fee covers transaction verification and secure facilitation of the exchange between the Seller and the Buyer.',
        p2: 'The party responsible for paying fees (buyer, seller, or split) is determined at the time the transfer request is created.'
      },
      
      buyer: {
        title: 'Buyer Responsibilities',
        p1: 'By using Middleman\'s services as a buyer, you agree to:',
        items: [
          'Provide accurate and complete information during registration',
          'Make payment within the specified timeframe after transfer confirmation',
          'Accept tickets transferred via the official platform (Ticketmaster, etc.)',
          'Not dispute payment after receiving valid tickets'
        ]
      },
      
      seller: {
        title: 'Seller Responsibilities',
        p1: 'By using Middleman\'s services as a seller, you agree to:',
        items: [
          'Only sell tickets you legitimately own',
          'Transfer tickets to Middleman within the agreed timeframe',
          'Guarantee ticket authenticity and validity',
          'Not cancel a transaction after buyer payment confirmation'
        ]
      },
      
      prohibited: {
        title: 'Prohibited Conduct',
        p1: 'The following conduct is strictly prohibited on Middleman:',
        items: [
          'Selling counterfeit, stolen, or fraudulent tickets',
          'Providing false or misleading information',
          'Attempting to circumvent the transaction process',
          'Using the platform for money laundering or other illegal activities',
          'Harassing or threatening other users or Middleman staff'
        ]
      },
      
      disputes: {
        title: 'Dispute Resolution',
        p1: 'In case of dispute between buyer and seller, Middleman will act as mediator to attempt to resolve the situation amicably. Our decision in case of dispute will be final and binding.',
        p2: 'If fraud is proven, funds will be refunded to the victim and the fraudulent account will be permanently banned.'
      },
      
      liability: {
        title: 'Limitation of Liability',
        p1: 'Middleman shall not be held liable for:',
        items: [
          'Event cancellation, modification, or postponement',
          'Ticket quality or viewing conditions',
          'Indirect damages resulting from platform use',
          'Technical service interruptions'
        ]
      },
      
      contact: {
        title: 'Contact',
        p1: 'For any questions regarding these Terms of Service, please contact us at:'
      }
    },
    
    // Privacy Policy
    privacy: {
      pageTitle: 'Middleman Privacy Policy',
      lastUpdated: 'Last updated: January 2025',
      
      intro: {
        title: 'Introduction',
        p1: 'At Middleman, we are committed to protecting your personal information in accordance with Quebec\'s Law 25 (Act respecting the protection of personal information in the private sector) and all other applicable laws.',
        p2: 'This Privacy Policy explains how we collect, use, store, and protect your personal information.'
      },
      
      collection: {
        title: 'Information Collection',
        p1: 'We collect the following information:',
        items: [
          'Full name and contact information (email, phone)',
          'Account and password information',
          'Transaction information (purchases, sales)',
          'Communications with our customer service',
          'Technical information (IP address, browser type)'
        ]
      },
      
      use: {
        title: 'Use of Information',
        p1: 'Your information is used to:',
        items: [
          'Process your transactions securely',
          'Verify your identity and prevent fraud',
          'Communicate with you about your transactions',
          'Improve our services',
          'Comply with our legal obligations'
        ]
      },
      
      sharing: {
        title: 'Information Sharing',
        p1: 'We do not sell your personal information. We share it only:',
        items: [
          'With your consent',
          'With our technical service providers (hosting, payments)',
          'When required by law or court order',
          'To protect our rights or safety of users'
        ]
      },
      
      security: {
        title: 'Data Security',
        p1: 'We implement appropriate technical and organizational measures to protect your information, including:',
        items: [
          'Communications encryption (SSL/TLS)',
          'Two-factor authentication (2FA)',
          'Regular security audits',
          'Restricted access to personal data'
        ]
      },
      
      rights: {
        title: 'Your Rights',
        p1: 'Under Law 25, you have the right to:',
        items: [
          'Access your personal information',
          'Request correction of inaccurate information',
          'Request deletion of your data',
          'Withdraw your consent',
          'Request data portability'
        ],
        p2: 'To exercise these rights, contact us at the address below.'
      },
      
      retention: {
        title: 'Data Retention',
        p1: 'We retain your information only as long as necessary to fulfill the purposes described in this policy, or as required by law. Transaction data is retained for 7 years for legal purposes.'
      },
      
      cookies: {
        title: 'Cookies',
        p1: 'We use cookies to improve your experience on our site. You can manage your cookie preferences through our consent banner.'
      },
      
      contact: {
        title: 'Contact',
        p1: 'For any questions about this Privacy Policy or to exercise your rights:'
      }
    }
  },
  
  fr: {
    // Conditions d'utilisation
    terms: {
      pageTitle: 'Conditions d\'utilisation de Middleman',
      lastUpdated: 'Dernière mise à jour : Janvier 2025',
      
      intro: {
        title: 'Introduction',
        p1: 'Les présentes Conditions d\'utilisation (les « Conditions ») constituent un contrat entre vous (ci-après désigné, selon le contexte, l\'« utilisateur », le « membre », l\'« acheteur » ou le « vendeur ») et Middleman. Ces Conditions régissent votre utilisation du site ainsi que des services offerts par Middleman.',
        p2: 'En tout temps, Middleman se réserve le droit de modifier les présentes Conditions. Votre utilisation du site confirme que vous acceptez de respecter ces Conditions telles qu\'elles sont énoncées et modifiées de temps à autre. Si vous ne pouvez vous engager à respecter ces Conditions, vous devez cesser immédiatement d\'utiliser le site et les services offerts par Middleman.'
      },
      
      service: {
        title: 'Nature du service offert',
        intermediary: {
          title: 'Service d\'intermédiaire et de vérification de validité',
          p1: 'Middleman agit en tant qu\'intermédiaire de confiance facilitant les transactions sécurisées de billets numériques vérifiables émis par des billetteries autorisées (notamment Ticketmaster, Evenko et autres plateformes similaires) entre un vendeur et un acheteur agissant en leur nom personnel (à titre de particulier).',
          p2: 'Nous agissons exclusivement comme intermédiaire en assurant la validité des billets échangés afin de réduire les risques de fraude et d\'offrir une transaction sécurisée entre le Vendeur et l\'Acheteur. Middleman vérifie la validité apparente des billets au moment de la transaction, sans garantie additionnelle.',
          p3: 'Middleman n\'est pas un vendeur ni un revendeur de billets.'
        },
        cancellation: {
          title: 'Responsabilité en cas d\'annulation d\'événement',
          p1: 'En cas d\'annulation ou de modification de l\'événement, Middleman n\'assume aucune responsabilité pour l\'organisation de l\'événement. Les détenteurs de billets doivent se référer directement à l\'organisation ayant émis initialement les billets ou à l\'organisateur de l\'événement pour toute demande d\'information, de report ou de remboursement. Le remboursement sera effectué selon les modalités prévues par l\'émetteur original du billet.'
        },
        role: {
          title: 'Rôle d\'intermédiaire',
          p1: 'Middleman agit uniquement en tant qu\'intermédiaire de transaction. Bien que nous assurions la validité des billets au moment de la transaction, Middleman n\'assume aucune responsabilité quant à l\'organisation de l\'événement ou à tout changement effectué par l\'organisateur.'
        },
        limitation: {
          title: 'Limitation',
          p1: 'Middleman ne pourra être tenu responsable de toute perte financière, dommage ou autre préjudice résultant de l\'annulation ou du report de l\'événement, ni de litiges entre le Vendeur et l\'Acheteur concernant l\'utilisation ou la valeur des billets.'
        }
      },
      
      eligibility: {
        title: 'Admissibilité',
        p1: 'Les services Middleman sont réservés aux personnes âgées d\'au moins dix-huit (18) ans, ou ayant atteint l\'âge de la majorité légale dans leur juridiction. Toute personne s\'inscrivant sur le site doit être en mesure de fournir des documents de vérification d\'identité sur demande.'
      },
      
      fees: {
        title: 'Frais de plateforme',
        p1: 'Middleman facture des frais de service selon le montant total du transfert : 6,95$ pour les transferts de 125$ et moins, 9,95$ pour les transferts de 125,01$ à 250$, et 19,95$ pour les transferts de 250,01$ et plus. Ces frais couvrent la vérification de la transaction et la facilitation sécurisée de l\'échange entre le Vendeur et l\'Acheteur.',
        p2: 'La partie responsable du paiement des frais (acheteur, vendeur ou partagé) est déterminée au moment de la création de la demande de transfert.'
      },
      
      buyer: {
        title: 'Responsabilités de l\'acheteur',
        p1: 'En utilisant les services Middleman en tant qu\'acheteur, vous acceptez de:',
        items: [
          'Fournir des informations exactes et complètes lors de l\'inscription',
          'Effectuer le paiement dans le délai spécifié après la confirmation du transfert',
          'Accepter les billets transférés via la plateforme officielle (Ticketmaster, etc.)',
          'Ne pas contester le paiement après avoir reçu des billets valides'
        ]
      },
      
      seller: {
        title: 'Responsabilités du vendeur',
        p1: 'En utilisant les services Middleman en tant que vendeur, vous acceptez de:',
        items: [
          'Ne vendre que des billets dont vous êtes légitimement propriétaire',
          'Transférer les billets à Middleman dans le délai convenu',
          'Garantir l\'authenticité et la validité des billets',
          'Ne pas annuler une transaction après la confirmation du paiement de l\'acheteur'
        ]
      },
      
      prohibited: {
        title: 'Conduites interdites',
        p1: 'Les conduites suivantes sont strictement interdites sur Middleman:',
        items: [
          'Vendre des billets contrefaits, volés ou frauduleux',
          'Fournir des informations fausses ou trompeuses',
          'Tenter de contourner le processus de transaction',
          'Utiliser la plateforme pour le blanchiment d\'argent ou d\'autres activités illégales',
          'Harceler ou menacer d\'autres utilisateurs ou le personnel de Middleman'
        ]
      },
      
      disputes: {
        title: 'Résolution des litiges',
        p1: 'En cas de litige entre l\'acheteur et le vendeur, Middleman agira en tant que médiateur pour tenter de résoudre la situation à l\'amiable. Notre décision en cas de litige sera finale et exécutoire.',
        p2: 'Si une fraude est prouvée, les fonds seront remboursés à la victime et le compte frauduleux sera définitivement banni.'
      },
      
      liability: {
        title: 'Limitation de responsabilité',
        p1: 'Middleman ne pourra être tenu responsable de:',
        items: [
          'L\'annulation, la modification ou le report de l\'événement',
          'La qualité des billets ou les conditions de visionnement',
          'Les dommages indirects résultant de l\'utilisation de la plateforme',
          'Les interruptions techniques du service'
        ]
      },
      
      contact: {
        title: 'Contact',
        p1: 'Pour toute question concernant ces Conditions d\'utilisation, veuillez nous contacter à:'
      }
    },
    
    // Politique de confidentialité
    privacy: {
      pageTitle: 'Politique de confidentialité de Middleman',
      lastUpdated: 'Dernière mise à jour : Janvier 2025',
      
      intro: {
        title: 'Introduction',
        p1: 'Chez Middleman, nous nous engageons à protéger vos renseignements personnels conformément à la Loi 25 du Québec (Loi sur la protection des renseignements personnels dans le secteur privé) et à toutes les autres lois applicables.',
        p2: 'Cette Politique de confidentialité explique comment nous recueillons, utilisons, conservons et protégeons vos renseignements personnels.'
      },
      
      collection: {
        title: 'Collecte des renseignements',
        p1: 'Nous recueillons les renseignements suivants:',
        items: [
          'Nom complet et coordonnées (courriel, téléphone)',
          'Informations de compte et mot de passe',
          'Informations sur les transactions (achats, ventes)',
          'Communications avec notre service à la clientèle',
          'Informations techniques (adresse IP, type de navigateur)'
        ]
      },
      
      use: {
        title: 'Utilisation des renseignements',
        p1: 'Vos renseignements sont utilisés pour:',
        items: [
          'Traiter vos transactions de manière sécuritaire',
          'Vérifier votre identité et prévenir la fraude',
          'Communiquer avec vous concernant vos transactions',
          'Améliorer nos services',
          'Respecter nos obligations légales'
        ]
      },
      
      sharing: {
        title: 'Partage des renseignements',
        p1: 'Nous ne vendons pas vos renseignements personnels. Nous les partageons uniquement:',
        items: [
          'Avec votre consentement',
          'Avec nos fournisseurs de services techniques (hébergement, paiements)',
          'Lorsque requis par la loi ou une ordonnance du tribunal',
          'Pour protéger nos droits ou la sécurité des utilisateurs'
        ]
      },
      
      security: {
        title: 'Sécurité des données',
        p1: 'Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos informations, incluant:',
        items: [
          'Cryptage des communications (SSL/TLS)',
          'Authentification à deux facteurs (2FA)',
          'Audits de sécurité réguliers',
          'Accès restreint aux données personnelles'
        ]
      },
      
      rights: {
        title: 'Vos droits',
        p1: 'En vertu de la Loi 25, vous avez le droit de:',
        items: [
          'Accéder à vos renseignements personnels',
          'Demander la correction d\'informations inexactes',
          'Demander la suppression de vos données',
          'Retirer votre consentement',
          'Demander la portabilité de vos données'
        ],
        p2: 'Pour exercer ces droits, contactez-nous à l\'adresse ci-dessous.'
      },
      
      retention: {
        title: 'Conservation des données',
        p1: 'Nous conservons vos renseignements seulement aussi longtemps que nécessaire pour atteindre les objectifs décrits dans cette politique, ou tel que requis par la loi. Les données de transaction sont conservées pendant 7 ans à des fins légales.'
      },
      
      cookies: {
        title: 'Témoins (Cookies)',
        p1: 'Nous utilisons des témoins (cookies) pour améliorer votre expérience sur notre site. Vous pouvez gérer vos préférences de témoins via notre bannière de consentement.'
      },
      
      contact: {
        title: 'Contact',
        p1: 'Pour toute question concernant cette Politique de confidentialité ou pour exercer vos droits:'
      }
    }
  }
};
